import type { ScopeContext, Event, EventHint } from '@sentry/core';
import { parseError } from '@snapchat/core';
import merge from 'lodash/merge.js';

import type { BaseEventListenerProps } from './AbstractEventListener';
import { AbstractEventListener } from './AbstractEventListener';
import type { BasePermission, EmptyObject, LoggedEvent } from './loggingTypes';
import { LoggingEventType } from './loggingTypes';

/**
 * Context for sentry logs.
 *
 * Note that `extras` has UNSTRUCTURED data, where as `contexts` has only a flat map of strings
 */
export interface SentryContext extends Partial<ScopeContext> {}

export type SentryEventCapturer = (event: Event, context: EventHint) => void;
export type SentryErrorCapturer = (error: Error, context: EventHint) => void;

export interface SentryLoggingContext {
  sentry?: SentryContext;
}
export interface SentryEventListenerProps<
  LoggingContext extends SentryLoggingContext,
  CustomEvents extends EmptyObject
> extends BaseEventListenerProps<LoggingContext, CustomEvents> {
  /**
   * Returns a `SentryContext` extracted from the generic `LoggingContext`.
   *
   * I.e. if you have generic fields in your context, and you want to slot them correctly into
   * Sentry specific context (fingerprints, tags, etc), this is where you do it.
   */
  getContext?: (
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ) => Partial<ScopeContext>;

  /** Formatter for the custom events directly into the sentry event format. */
  customEventFormat?: (
    event: CustomEvents,
    context: Partial<LoggingContext>
  ) => [Event, EventHint] | null | undefined;
}

/** Base class for sentry event listeners. */
export abstract class AbstractSentryEventListener<
  LoggingContext extends SentryLoggingContext = SentryLoggingContext,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> extends AbstractEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  protected readonly props: SentryEventListenerProps<LoggingContext, CustomEvents>;
  private captureException: SentryErrorCapturer | undefined;
  private captureEvent: SentryEventCapturer | undefined;

  constructor(props: SentryEventListenerProps<LoggingContext, CustomEvents>) {
    super({
      ...props,
      requiredPermissions: ['logging'],
    });
    this.props = props;
  }

  /** @override */
  protected init = async (): Promise<void> => {
    this.captureException = await this.getExceptionCapturer();
    this.captureEvent = await this.getEventCapturer();
  };

  /** @override */
  protected logEventInternal(
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ): void {
    const loggerContext: Record<string, string | undefined> = {};
    if ('component' in event) loggerContext.component = event.component;
    if ('action' in event) loggerContext.action = event.action;
    if ('label' in event) loggerContext.label = event.label;
    const eventHint: EventHint = {
      captureContext: merge(
        { contexts: { logger: loggerContext } },
        context.sentry,
        // NOTE: We let the `getContext` function have the final say on any
        // context overrides.
        this.props.getContext?.(event, context)
      ),
    };
    switch (event.type) {
      case LoggingEventType.DEBUG: {
        this.captureEvent!({ message: event.message, level: 'debug' }, eventHint);
        break;
      }

      case LoggingEventType.INFO: {
        // NOTE: Component is already in the `eventHint` but is duplicated here for context.
        // Otherwise events like `{component: 'AdBlock', action: 'Exists'}` do not show up
        // well in the UI.
        const message = `${event.component}: ${event.action}`;
        this.captureEvent!({ message: message, level: 'info' }, eventHint);
        break;
      }

      case LoggingEventType.WARNING: {
        this.captureEvent!({ message: event.message, level: 'warning' }, eventHint);
        break;
      }

      case LoggingEventType.ERROR: {
        const error = event.message
          ? new Error(event.message)
          : parseError(event.error ?? 'UnknownError');

        this.captureException!(error, eventHint);
        break;
      }

      case LoggingEventType.TIMING:
      // fallthrough
      case LoggingEventType.VALUE: {
        // Nothing here. Note that Sentry does have ability to write metrics
        // into it via:
        //
        //  measurements: {
        //   [event.variable]: {
        //     unit: 'none' as MeasurementUnit, // can use 'milliseconds' for timing events.
        //     value: event.value,
        //   },
        // },
        //
        // But you can't see nor visualize this data in Sentry UI under events.
        // It is possible to attach this data to performance metric events
        // under `tracing`, but the logging event bus isn't setup to do tracing
        // (nor should it be), so currently nothing is logged in this case.
        //
        // If you need to emit metrics, we suggesting using Custom events and manually
        // setting up tracing and metric emitting:
        // Traces: https://docs.sentry.io/platforms/javascript/tracing/
        // Metrics: https://docs.sentry.io/platforms/javascript/tracing/instrumentation/performance-metrics/
        //
        break;
      }

      case LoggingEventType.CUSTOM: {
        if (!this.props.customEventFormat) {
          break;
        }
        const [customEvent, customContext] = this.props.customEventFormat(event, context) ?? [];
        if (!customEvent) {
          break;
        }

        this.captureEvent!(customEvent, merge({}, eventHint, customContext));
        break;
      }
    }
  }

  /** Lazy loads the library for capturing errors into sentry. */
  protected abstract getExceptionCapturer: () => Promise<SentryErrorCapturer>;

  /** Lazy loads the library for capturing logs into sentry. */
  protected abstract getEventCapturer: () => Promise<SentryEventCapturer>;

  /** @override */
  protected flushInternal: () => Promise<unknown> = () => Promise.resolve();
}
